/** One band of the price distribution, in cents; the last band can be open-ended. */
export type PriceBand = {
  minimumPriceCents: number;
  maximumPriceCents: number | null;
  listingCount: number;
};

/** Bars across the price range panel. */
export const PRICE_BINS = 34;
/** Homes above this share of the market count as outliers and fall off the axis. */
const AXIS_COVERAGE = 0.98;
/** An axis never shrinks below this, so a small area still has room to filter. */
const AXIS_FLOOR = 250_000;
/** The axis only trims the top when it would shorten the line by more than this. */
const OUTLIER_STRETCH = 1.25;
const FALLBACK_AXIS = 1_000_000;

const niceStep = (value: number) => {
  if (value <= 1_000_000) return 50_000;
  if (value <= 2_500_000) return 100_000;
  return 250_000;
};

/** Rounds up to the next clean tick for the axis's size. */
export const roundUpToNice = (value: number): number => {
  const step = niceStep(value);
  return Math.ceil(value / step) * step;
};

const bandTop = (band: PriceBand, bands: readonly PriceBand[]): number => {
  if (band.maximumPriceCents != null) return band.maximumPriceCents;
  const first = bands.find((item) => item.maximumPriceCents != null);
  const width = first ? first.maximumPriceCents! - first.minimumPriceCents : 10_000_000;
  return band.minimumPriceCents + width;
};

/**
 * The right end of the price line, in dollars. It reaches the most expensive
 * home unless a few outliers would stretch the line far past where nearly
 * every home sits; then it stops at the 98th percentile and the open "+" end
 * still includes the rest. Always a clean round number and at least the floor.
 */
export const priceAxisMax = (bands: readonly PriceBand[] | undefined): number => {
  const listed = (bands ?? []).filter((band) => band.listingCount > 0);
  const total = listed.reduce((sum, band) => sum + band.listingCount, 0);
  if (listed.length === 0 || total === 0) return FALLBACK_AXIS;
  const fullMax = Math.max(...listed.map((band) => bandTop(band, bands ?? [])));
  let covered = 0;
  let coverageMax = fullMax;
  for (const band of listed) {
    covered += band.listingCount;
    if (covered >= AXIS_COVERAGE * total) {
      coverageMax = bandTop(band, bands ?? []);
      break;
    }
  }
  const trimmed = fullMax > coverageMax * OUTLIER_STRETCH ? coverageMax : fullMax;
  return Math.max(AXIS_FLOOR, roundUpToNice(trimmed / 100));
};

/**
 * Spreads the bands over equal bins from $0 to the axis end. A band that
 * straddles bins is split in proportion; everything past the end lands in the
 * last bin, which is the open "+" bin.
 */
export const binPriceBands = (
  bands: readonly PriceBand[] | undefined,
  axisMax: number,
  bins = PRICE_BINS,
): number[] => {
  const counts = Array.from({ length: bins }, () => 0);
  if (!bands || axisMax <= 0) return counts;
  const width = axisMax / bins;
  for (const band of bands) {
    if (band.listingCount <= 0) continue;
    const lo = band.minimumPriceCents / 100;
    const hi = Math.max(lo + 1, bandTop(band, bands) / 100);
    const span = hi - lo;
    for (let index = 0; index < bins; index++) {
      const binLo = index * width;
      const binHi = index === bins - 1 ? Number.POSITIVE_INFINITY : (index + 1) * width;
      const overlap = Math.min(hi, binHi) - Math.max(lo, binLo);
      if (overlap > 0) counts[index] += (band.listingCount * overlap) / span;
    }
  }
  return counts;
};

/** 250000 → "250k"; 1000000 → "1M"; 1350000 → "1.35M". */
export const shortPrice = (dollars: number): string => {
  const thousands = Math.round(dollars / 1000);
  return thousands >= 1000 ? `${Math.round(thousands / 10) / 100}M` : `${thousands}k`;
};

/** The Price chip's text; null bounds are open. */
export const priceChipLabel = (min: number | null, max: number | null): string => {
  if (min == null && max == null) return 'Any';
  if (min == null) return `Up to ${shortPrice(max ?? 0)}`;
  if (max == null) return `${shortPrice(min)}+`;
  return `${shortPrice(min)} - ${shortPrice(max)}`;
};
